/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow

import type { Element } from 'react';

import React from 'react';
import { Button, Col, FormGroup, Label, Row } from 'reactstrap';
import { Field } from 'redux-form';
import { Field as ModelFormField } from '#components/pages/Models/Model/support/form/index.js';
import { FieldFeedback, RenderInput } from '#components/support/forms/utility/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';

type Props = {|
  field: Object,
  projects: string[],
  aiStatus?: 'none' | 'proposed' | 'accepted',
|};

const ProjectSelector = ({
  projects,
  field,
  aiStatus = 'none',
}: Props): Element<typeof FormGroup> => {
  const { form, name, label, tipId, type } = field;
  const hasExistingProjects = projects.length !== 0;
  // Hooks run on every render, before the early return below (rules of hooks): the same mounted
  // selector sees its field's review status change -- Reject / Reject all / Edit move a proposal to
  // 'none' -- so a hook placed after the return would change the hook count on a live instance.
  const [isNew, setIsNew] = React.useState(!hasExistingProjects);

  // When the field carries an AI proposal (or an accepted value), render it through the shared
  // ModelFormField so it gets the same marking chip, provenance, and Accept/Reject/Edit controls as
  // every other aiExposed field (#154, UI design §3.1/AU-11). Without this, a proposed PROJECT_NAME
  // counts toward the run-gate but shows no per-field control to clear it. The bespoke create-new /
  // select-existing control below is only for the ordinary, unmarked editing state.
  if (aiStatus !== 'none') {
    return <ModelFormField field={field} />;
  }
  const showNewProjectToggle = hasExistingProjects;
  const newOrExistingText = isNew ? 'Select Existing' : 'Create New';
  const fieldType = isNew ? type : 'select';
  const options = projects.map(p => (
    <option key={p} value={p}>
      {p}
    </option>
  ));
  const optionsComponents = !isNew ? (
    <>
      <option />
      {options}
    </>
  ) : null;

  return (
    <FormGroup>
      <Label htmlFor={name}>
        {label}
        {tipId && (
          <span style={{ marginLeft: '0.25em' }}>
            <Tip tipId={tipId} />
          </span>
        )}
      </Label>
      <Row>
        <Col lg="9">
          <Field form={form} id={name} name={name} type={fieldType} component={RenderInput}>
            {optionsComponents}
          </Field>
          <FieldFeedback name={name} form={form} />
        </Col>
        <Col className="pl-0 pr-0">
          {showNewProjectToggle && (
            <Button
              id={isNew ? 'selectExistingPrjBtn' : 'createNewProjBtn'}
              color="link"
              onClick={() => setIsNew(!isNew)}
              className="pl-0 pr-0"
            >
              {newOrExistingText}
            </Button>
          )}
        </Col>
      </Row>
    </FormGroup>
  );
};

export default ProjectSelector;
